/**
 * Save + validation logic for completing a Risk Assessment, extracted verbatim
 * from the legacy ReviewRiskAssessment stepper so the single-view completion
 * produces a byte-identical saveRiskAssessmentHistory payload. The hazard/control
 * graph is the untyped SafetyNest reducer state, so params stay `any`.
 */

const allHazardsOf = (details: any) => ({
    ...(details?.hazards ?? {}),
    ...(details?.customHazards ?? {}),
});

function hasScoreGaps(bucket: any): boolean {
    return Object.keys(bucket ?? {}).some(
        (x) =>
            bucket[x].severityScorePreControl === 0 ||
            bucket[x].severityScorePostControl === 0 ||
            bucket[x].likelihoodScorePreControl === 0 ||
            bucket[x].likelihoodScorePostControl === 0
    );
}

function hasUnsetControlInPlace(bucket: any): boolean {
    return Object.keys(bucket ?? {}).some((hazard) =>
        [...(bucket[hazard]?.hazardControls ?? []), ...(bucket[hazard]?.customHazardControls ?? [])].some((control: any) => control?.inPlace === undefined)
    );
}

/**
 * Returns an error message if the assessment isn't ready to complete, else null.
 * Mirrors the gates in ReviewRiskAssessment.onNext across the EVALUATE + RECORD steps.
 */
export function validateForComplete(
    details: any,
    { requiresDueDate, nextDueDate, isEditMode }: { requiresDueDate: boolean; nextDueDate: any; isEditMode: boolean | null }
): string | null {
    if (hasScoreGaps(details?.hazards) || hasScoreGaps(details?.customHazards)) {
        return "You must score each hazard.";
    }

    if (hasUnsetControlInPlace(details?.hazards) || hasUnsetControlInPlace(details?.customHazards)) {
        return "You must select whether a control is in place or not.";
    }

    const allHazards = allHazardsOf(details);
    const processControls = (hazardId: string, controlsKey: string) => {
        const controls = allHazards[hazardId]?.[controlsKey];
        if (!controls) return 0;
        return controls.reduce((prev: number, curr: any) => {
            if (!curr?.inPlace) {
                if (
                    !curr?.control?.hasOwnProperty("controlAction") ||
                    !curr?.control?.controlAction[0]?.hasOwnProperty("actionPresetDescription") ||
                    curr?.control?.controlAction[0]?.actionPresetDescription.length === 0
                ) {
                    if (
                        !curr?.riskAssessmentControl?.riskAssessmentControlAction.hasOwnProperty("actionPresetDescription") ||
                        curr?.riskAssessmentControl?.riskAssessmentControlAction.actionPresetDescription.length === 0
                    ) {
                        return prev + 1;
                    }
                }
            }
            return prev;
        }, 0);
    };

    const missingPerHazard = Object.keys(allHazards).map((id) => ({
        name: allHazards[id]?.name,
        count: processControls(id, "hazardControls") + processControls(id, "customHazardControls"),
    }));
    const missingTotal = missingPerHazard.reduce((sum, x) => sum + x.count, 0);

    if (!isEditMode && missingTotal > 0) {
        const names = missingPerHazard.filter((x) => x.count > 0 && x.name).map((x) => x.name);
        const where = names.length ? ` Check ${names.slice(0, 2).join(", ")}${names.length > 2 ? ` and ${names.length - 2} more` : ""}.` : "";
        return missingTotal === 1
            ? `Enter a description for the action in the Action plan.${where}`
            : `Enter a description for all ${missingTotal} actions in the Action plan.${where}`;
    }

    if (requiresDueDate && nextDueDate === null) {
        return "Next due date must be set.";
    }

    return null;
}

/**
 * Groups an assessment's attachments into the hazardFiles shape buildSavePayload
 * expects: keyed the way the reducer keys hazards, which is by hazard id for a
 * library hazard and custom hazard id for a custom one — not by hazard row id.
 */
export function hazardFilesFromAttachments(hazardRows: any[], attachments: any[]): Record<string, number[]> {
    return Object.fromEntries(
        (hazardRows ?? []).map((row) => [
            row.hazardId ?? row.customHazardId,
            (attachments ?? []).filter((x) => x.riskAssessmentHazardId === row.id).map((x) => x.azureFileId),
        ])
    );
}

/**
 * Builds the saveRiskAssessmentHistory request body. Mirrors the shaping in
 * ReviewRiskAssessment.onNext exactly (including its in-place mutation of the
 * hazardControls arrays).
 */
export function buildSavePayload(
    details: any,
    {
        nextDueDate,
        isEditMode,
        riskAssessmentHistoryId,
        allPeople,
        hazardFiles = {},
    }: { nextDueDate: any; isEditMode: boolean | null; riskAssessmentHistoryId: any; allPeople: any[]; hazardFiles?: Record<string, number[]> }
): any {
    return {
        nextDueDate,
        siteExternalId: details.siteExternalId,
        riskAssessmentId: details.id,
        RiskAssessmentHistoryId: isEditMode ? riskAssessmentHistoryId : null,
        peoples: Object.keys(details.people).map((people) => ({
            id: details.people[people].id,
            name: allPeople?.filter((p) => p.id === details.people[people].id)[0]?.name || null,
        })),
        riskAssessmentHazards: Object.keys(details.hazards)
            .filter((hazardKey) => !isNaN(Number(hazardKey)))
            .map((hazardKey) => {
                const hazardToCheck = details.hazards[hazardKey];
                hazardToCheck.hazardControls = hazardToCheck.hazardControls.map((control: any) => {
                    if (control.riskAssessmentControl) {
                        control.riskAssessmentControl.riskAssessmentControlAction.controlId = control.control?.id ?? undefined;
                    }
                    if (isNaN(control.control?.id)) {
                        delete control.control.id;
                    }
                    if (isNaN(control.id)) {
                        delete control.id;
                    }
                    if (
                        isNaN(control.riskAssessmentControl?.riskAssessmentControlAction?.actionPresetId) &&
                        control.riskAssessmentControl?.riskAssessmentControlAction
                    ) {
                        delete control.riskAssessmentControl.riskAssessmentControlAction.actionPresetId;
                    }
                    if (
                        isNaN(control.riskAssessmentControl?.riskAssessmentControlAction?.controlId) &&
                        control.riskAssessmentControl?.riskAssessmentControlAction
                    ) {
                        delete control.riskAssessmentControl.riskAssessmentControlAction.controlId;
                    }
                    if (control.inPlace) {
                        delete control.riskAssessmentControl;
                    }
                    return control;
                });
                hazardToCheck.azureFileIds = hazardFiles[hazardKey] ?? [];
                return hazardToCheck;
            }),
        customPeoples: Object.keys(details.customPeople).map((people) => {
            const customPeople = details.customPeople[people];
            delete customPeople.id;
            return customPeople;
        }),
        customRiskAssessmentHazards: Object.keys(details.customHazards).map((customHazard) => {
            const customHazardToCheck = details.customHazards[customHazard];
            customHazardToCheck.hazardControls?.map((control: any) => {
                if (isNaN(control.id)) {
                    delete control.id;
                }
                if (isNaN(control.control.id)) {
                    delete control.control.id;
                }
            });
            if (isNaN(customHazardToCheck.id)) {
                delete customHazardToCheck.id;
            }
            customHazardToCheck.azureFileIds = hazardFiles[customHazard] ?? [];
            return customHazardToCheck;
        }),
        updatedActions: details.incompleteActions
            ?.filter((x: any) => x.hasUpdated)
            .map((action: any) => ({
                id: action.id,
                dueDate: action.dueDate,
                assignedEmployeeExternalId: action.employeeId,
                rating: action.rating,
            })),
    };
}
